import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import {
  cellRenders,
  filterComponents,
  filterMethods,
} from "./utils/filtersAndCells";
import Button from "../components/CustomButton/CustomButton.jsx";
import { Row, Col } from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import Card from "../components/Card/Card";
import { constants } from "../redux/callReports/actions";
import { commonConstants } from "../redux/common/actions";
import { getFormattedUser } from "../services/auth";
import NotificationSystem from "react-notification-system";
import { style as notificationStyling } from "../variables/Variables.jsx";
import CustomReactTablePagination from "./Components/CustomReactTablePagination";

const {
  FETCH_LIST,
  UPDATE_DATE_FILTER,
  DELETE_CALL_REPORT,
  UPDATE_CALL_REPORTS_FILTERS,
  RESET_CALL_REPORTS_FILTERS,
} = constants;
const { REQUEST } = commonConstants;
const Datetime = require("react-datetime");
const headerStyle = {
  textAlign: "center",
  fontWeight: "bold",
  color: "#333333",
};
const cellStyle = { textAlign: "center" };

const CallReports = ({ history }) => {
  const {
    list,
    toDateFilter,
    fromDateFilter,
    loading,
    error,
    success,
    tableState,
  } = useSelector((state) => state.callReports);
  const dispatch = useDispatch();
  const notificationSystem = useRef();
  const [callTakerId, setCallTakerId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [_notificationSystem, setNotificationSystem] = useState({});

  // initial page fetch
  useEffect(() => {
    dispatch({
      type: FETCH_LIST + REQUEST,
      payload: { fromDate: fromDateFilter, toDate: toDateFilter },
    });
    setNotificationSystem(notificationSystem);
    return () => {};
  }, []);
  // retrieve user
  const [hasAdmin, setHasAdmin] = useState(false);
  const [hasEdit, setHasEdit] = useState(false);
  useEffect(() => {
    const getUser = async () => {
      const user = await getFormattedUser();
      const hasAdmin = user && user.roles && user.roles.includes("SuperAdmin");
      const hasEdit =
        user &&
        user.roles &&
        (user.roles.includes("ProviderAdmin") ||
          user.roles.includes("HrAdmin"));
      setHasAdmin(hasAdmin);
      setHasEdit(hasEdit);
      setCallTakerId(user.id);
    };
    getUser().then();
  }, []);
  // handle ajax side effects
  useEffect(() => {
    const notificationSystem = _notificationSystem.current;
    if (notificationSystem && success.message) {
      notificationSystem.addNotification({
        message: success.message,
        level: "info",
        position: "tc",
      });
    }
    if (notificationSystem && error.message) {
      notificationSystem.addNotification({
        message: error.message,
        level: "error",
        position: "tc",
      });
    }
    return () => {};
  }, [success.message, error.message]);
  // handle onClick row navigation
  const [historyNavigation, setHistoryNavigation] = useState({
    enabled: false,
    id: null,
  });
  useEffect(() => {
    if (historyNavigation.enabled) {
      history.push(
        `/admin/view-call-entry/${historyNavigation.id}?readOnly=true`
      );
    }
    return () => {};
  }, [historyNavigation.enabled]);
  // handle scroll to top for notifications
  useEffect(() => {
    // THIS IS SCREWY - Desktop works fine, Mobile scrolls to top quickly then back to original position - 2x fixed this
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    return () => {};
  }, [success.message, error.message]);

  const renderDeleteConfirm = (callReportId, userId, dispatch) => {
    return setAlert(
      <SweetAlert
        showCancel
        style={{ display: "block", marginTop: "-100px" }}
        title="Confirm Deletion"
        onConfirm={() =>
          dispatch({
            type: DELETE_CALL_REPORT + REQUEST,
            payload: {
              callReportId,
              userId,
              startDate: fromDateFilter,
              endDate: toDateFilter,
            },
          }) && setAlert(null)
        }
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="danger"
      >
        {`Are you sure you want to delete call report #${callReportId}?`}
      </SweetAlert>
    );
  };

  const { binaryCell, easternTzDateCell } = cellRenders();
  const { BinaryFilter } = filterComponents();
  const { binaryFilterMethod, startsWithFilterMethod } = filterMethods();

  const columns = [
    { accessor: "id", show: false },
    { accessor: "callTakerId", show: false },
    {
      accessor: "listenerName",
      Header: "Listener Name",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
    },
    {
      accessor: "listenerEmail",
      Header: "Listener Email",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
    },
    {
      accessor: "name",
      Header: "Caller Name",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
    },
    {
      accessor: "isFrequentCaller",
      Header: "Frequent/Chronic Caller",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
      Cell: binaryCell,
      filterMethod: binaryFilterMethod,
      Filter: BinaryFilter,
    },
    {
      accessor: "provinceState",
      Header: "Province/State",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
    },
    {
      accessor: "city",
      Header: "City",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
    },
    {
      accessor: "cellPhone",
      Header: "Area Code",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
      Cell: (props) => {
        if (!props.value) return "";
        if (props.original.country === "AUSTRALIA") {
          return props.value.slice(-3);
        }
        return props.value.substring(0, 3);
      },
    },

    {
      accessor: "callReason",
      Header: "Summary Narrative",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
    },
    {
      accessor: "isSuicidalCall",
      Header: "Suicidal Call",
      editable: "never",
      searchable: true,
      style: cellStyle,
      headerStyle,
      Cell: binaryCell,
      filterMethod: binaryFilterMethod,
      Filter: BinaryFilter,
    },
    {
      accessor: "callStart",
      Header: "Call Start",
      editable: "never",
      type: "datetime",
      searchable: true,
      style: cellStyle,
      headerStyle,
      Cell: easternTzDateCell,
    },
    {
      accessor: "callEnd",
      Header: "Call End",
      editable: "never",
      type: "datetime",
      searchable: true,
      style: cellStyle,
      headerStyle,
      Cell: easternTzDateCell,
    },

    { accessor: "email", Header: "Email", show: false },
    { accessor: "isVeteran", Header: "Is Veteran", show: false },
    { accessor: "veteranBranch", Header: "Veteran Branch", show: false },
    {
      accessor: "yearsOfMilitaryService",
      Header: "Years of Military Service",
      show: false,
    },
    { accessor: "isDisabled", Header: "Is Disabled", show: false },
    {
      accessor: "receivedVaTreatment",
      Header: "Received VA Treatment",
      show: false,
    },
    { accessor: "gender", Header: "Gender", show: false },
    { accessor: "veteranRank", Header: "Veteran Rank", show: false },
    { accessor: "headTrauma", Header: "Head/Neck Trauma", show: false },
    {
      accessor: "yearsOfPoliceService",
      Header: "Years of Police Service",
      show: false,
    },
    { accessor: "departmentSize", Header: "Department Size", show: false },
    { accessor: "callerType", Header: "Caller Type", show: false },
    { accessor: "age", Header: "Age", show: false },
    { accessor: "coplineReferral", Header: "Copline Referral", show: false },
    { accessor: "impairment", Header: "Impairment", show: false },
    { accessor: "currentStruggle", Header: "Current Struggle", show: false },
    { accessor: "callWasReferred", Header: "Call was Referred?", show: false },
    { accessor: "referralDetails", Header: "Referral Details", show: false },
    { accessor: "returnCall", Header: "Return Call Requested?", show: false },
    {
      accessor: "returnCallTimezone",
      Header: "Return Call Timezone",
      show: false,
    },
    {
      accessor: "returnCallDetails",
      Header: "Return Call Details",
      show: false,
    },
    {
      accessor: "returnCallFollowUpNotes",
      Header: "Return Call Follow up Notes",
      show: false,
    },
    { accessor: "createdAt", show: false },
    { accessor: "updatedAt", show: false },
    { accessor: "callTakerId", show: false },
    { accessor: "hasUnresolvedReturnCalls", show: false },
    { accessor: "combatExperience", Header: "Combat Experience", show: false },
    {
      accessor: "treatedForPtsd",
      Header: "Diagnosed/Treated for PTSD",
      show: false,
    },
    {
      accessor: "otherMedicalCondition",
      Header: "Diagnosed/Treated other Medical Condition",
      show: false,
    },

    {
      Header: "Actions",
      accessor: "actions",
      sortable: false,
      filterable: false,
      style: cellStyle,
      headerStyle,
    },
  ];

  const formattedList = useMemo(() => {
    return list.map((l) => {
      return {
        ...l,
        actions: (
          // we've added some custom button actions
          <div className="actions-center">
            {/* use this button to add a edit kind of action */}
            {(l.callTakerId === callTakerId || hasAdmin || hasEdit) && (
              <Link to={`/admin/view-call-entry/${l.id}`}>
                <Button onClick={() => {}} bsStyle="warning" simple icon>
                  <i
                    className="pe-7s-pen action-icon"
                    style={{ color: "blue" }}
                  />
                </Button>{" "}
              </Link>
            )}
            {/* use this button to remove the data row */}
            {(hasAdmin || hasEdit) && (
              <Button
                onClick={() => renderDeleteConfirm(l.id, l.id, dispatch)}
                bsStyle="danger"
                simple
                icon
              >
                <i className="pe-7s-trash action-icon" />
              </Button>
            )}
          </div>
        ),
      };
    });
  }, [list]);

  const handleUpdateTableFilter = (field, value) =>
    dispatch({ type: UPDATE_CALL_REPORTS_FILTERS, field, value });

  const handleClearTableFilters = () =>
    dispatch({ type: RESET_CALL_REPORTS_FILTERS });

  return (
    <div className="main-content">
      <NotificationSystem
        ref={notificationSystem}
        style={notificationStyling}
      />
      {alert}
      <Card
        title={
          <Row style={{ paddingLeft: "15px" }}>
            <Col xs={12} sm={6}>
              <p>From Date: </p>
              <Datetime
                id="from-date-filter"
                name="fromDateFilter"
                value={fromDateFilter}
                timeFormat={false}
                onChange={(event) => {
                  dispatch({
                    type: FETCH_LIST + REQUEST,
                    payload: { fromDate: event._d, toDate: toDateFilter },
                  });
                }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <p>To Date: </p>
              <Datetime
                id="to-date-filter"
                name="toDateFilter"
                value={toDateFilter}
                timeFormat={false}
                onChange={(event) => {
                  dispatch({
                    type: FETCH_LIST + REQUEST,
                    payload: { fromDate: fromDateFilter, toDate: event._d },
                  });
                }}
              />
            </Col>
          </Row>
        }
        content={
          <>
            <Row>
              <Col></Col>
            </Row>
            <Row>
              <Col>
                <ReactTable
                  PaginationComponent={CustomReactTablePagination}
                  paginationProps={{ onClearFilters: handleClearTableFilters }}
                  filtered={tableState.filtered}
                  onFilteredChange={(filtered) =>
                    handleUpdateTableFilter("filtered", filtered)
                  }
                  sorted={tableState.sorted}
                  onSortedChange={(sorted) =>
                    handleUpdateTableFilter("sorted", sorted)
                  }
                  page={tableState.page}
                  onPageChange={(page) => handleUpdateTableFilter("page", page)}
                  pageSize={tableState.pageSize}
                  onPageSizeChange={(pageSize) =>
                    handleUpdateTableFilter("pageSize", pageSize)
                  }
                  showTotalPages={true}
                  canNextFromData={false}
                  data={formattedList}
                  columns={columns}
                  defaultSorted={[{ id: "callStart", desc: true }]}
                  defaultFilterMethod={startsWithFilterMethod}
                  getTrProps={(state, rowInfo) => {
                    if (rowInfo && rowInfo.row.hasUnresolvedReturnCalls) {
                      return { style: { background: "orange" } };
                    } else return {};
                  }}
                  getTdProps={(state, rowInfo, column) => {
                    if (column && column.id === "actions") {
                      return {};
                    } else if (rowInfo && rowInfo.row) {
                      return {
                        onClick: () =>
                          setHistoryNavigation({
                            enabled: true,
                            id: rowInfo.row.id,
                          }),
                      };
                    } else {
                      return {};
                    }
                  }}
                  filterable
                  defaultPageSize={100}
                  showPaginationTop
                  sortable
                  className="-striped -highlight row-pointer"
                  loading={loading.source === FETCH_LIST}
                />
              </Col>
            </Row>
          </>
        }
      />
    </div>
  );
};

// List.PropTypes = {
//   list: PropTypes.array.isRequired,
//   unfilteredList: PropTypes.array.isRequired,
//   toDateFilter: PropTypes.object.isRequired,
//   fromDateFilter: PropTypes.object.isRequired,
//   loading: PropTypes.object.isRequired,
//   error: PropTypes.object.isRequired,
//   success: PropTypes.object.isRequired,
//   dispatch: PropTypes.func.isRequired
// }

export default CallReports;
